import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Star, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    review: '',
    service: 'Wedding Photography'
  });

  // Fetch reviews on component mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, formData);
      
      toast.success("Review Submitted!", {
        description: "Thank you for your feedback! Your review has been posted.",
      });

      // Reset form and refresh reviews
      setFormData({
        name: '',
        rating: 5,
        review: '',
        service: 'Wedding Photography'
      });
      setShowForm(false);
      fetchReviews();
      
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error("Error Submitting Review", {
        description: "There was an error submitting your review. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            onClick={interactive ? () => handleRatingClick(star) : undefined}
          />
        ))}
      </div>
    );
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  return (
    <section id="reviews" className="py-8 sm:py-10 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
            Client Reviews
          </h2>
          {reviews.length > 0 && (
            <div className="flex items-center justify-center gap-2 mb-2">
              {renderStars(Math.round(calculateAverageRating()), false, 'w-4 h-4')}
              <span className="text-lg font-bold text-slate-800">
                {calculateAverageRating()}
              </span>
              <span className="text-slate-600 text-xs">
                ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          )}
          <p className="text-sm text-slate-600 max-w-2xl mx-auto">
            See what my clients say
          </p>
        </div>

        {/* Leave a Review Button */}
        {!showForm && (
          <div className="flex justify-center mb-6">
            <Button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-5 py-3 text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Leave a Review
            </Button>
          </div>
        )}

        {/* Review Form */}
        {showForm && (
          <Card className="max-w-xl mx-auto p-3 sm:p-4 mb-6 border-none shadow-lg bg-white">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Share Your Experience</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Your Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Service Used
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="Wedding Photography">Wedding Photography</option>
                  <option value="Engagement Session">Engagement Session</option>
                  <option value="Family Portrait">Family Portrait</option>
                  <option value="Gallery & Artistic">Gallery & Artistic</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Rating
                </label>
                {renderStars(formData.rating, true, 'w-8 h-8')}
              </div>

              <div>
                <label htmlFor="review" className="block text-sm font-medium text-slate-700 mb-2">
                  Your Review
                </label>
                <Textarea
                  id="review"
                  name="review"
                  required
                  value={formData.review}
                  onChange={handleChange}
                  placeholder="Tell us about your experience..."
                  rows={5}
                  className="resize-none"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 font-semibold disabled:opacity-50"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {isSubmitting ? 'Submitting...' : 'Submit Review'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="px-6"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Reviews Grid */}
        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-6xl mx-auto">
            {reviews.map((review) => (
              <Card
                key={review.id}
                className="p-3 border-none shadow-md hover:shadow-lg transition-shadow duration-300 bg-white"
              >
                {/* Rating */}
                <div className="flex items-center justify-between mb-2">
                  {renderStars(review.rating, false, 'w-3 h-3')}
                  <span className="text-[10px] text-slate-500">
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Review Text */}
                <p className="text-slate-700 text-xs mb-2 leading-relaxed line-clamp-3">
                  "{review.review}"
                </p>

                {/* Author & Service */}
                <div className="border-t pt-2">
                  <p className="font-semibold text-slate-800 text-xs">{review.name}</p>
                  <p className="text-[10px] text-cyan-600">{review.service}</p>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-slate-600 text-sm">
              Be the first to leave a review!
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Reviews;
